'use strict';

/**
 * Model for an object item
 *
 * @category   Model
 * @module     collection
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Html = require('base/Colido.Html');
var Model = require('base/Colido.Model');

var ObjectModel = Model.extend({
    api: '/objects',

    urlRoot: false, //needed to get proper api endpoint for models which are part of subcollection (catalog.objects...) with dynamic api urls

    defaults: function() {
        return this.collection.catalog.getObjectDefaults();
    },

    // defaults: function(){ //use function, cause objects are passed by reference
    //     return {
    //         modifiedTime: null,
    //         creationTime: null,
    //         objectId: '', //String
    //         properName: '', //String
    //         description: '', //String
    //         descriptionHtml: '', //String
    //         species: [], //Array of species objects {id: 1, name: 'Eisen', chemicalFormula: 'Fe', correlation: 'on'} (correlation is never set on the first species)
    //         status: '', //@todo add default status?
    //         images: [], //Objects
    //         storageId: null,
    //         storage: '',
    //         dimensionType: '',
    //         colors: [], //Strings
    //         colorSpecification: null,
    //         weight: null, //Float
    //         weightUnit: App.user ? App.user.get('defaultWeightUnit') : 'g',
    //         estimatedValue: null, //Float
    //         estimatedValueCurrency: App.user ? App.user.getCurrency() : 'eur',
    //         insuranceValue: null,
    //         insuranceValueCurrency: App.user ? App.user.getCurrency() : 'eur',
    //         retailPrice: null,
    //         retailPriceCurrency: App.user ? App.user.getCurrency() : 'eur',
    //         sizeWidth: 0, //(rounded to 2 decimal places; converted from 'cm' to UNIT)
    //         sizeHeight: 0,
    //         sizeDepth: 0,
    //         sizeUnit: App.user ? App.user.get('defaultSizeUnit') : 'cm',
    //         locality: null,
    //         _properNameIsAutogenerated: true,
    //         objectHistory: [], //Objects
    //         previousOwners: '', //String
    //         objectStatus: 'in-collection',
    //         objectStatusRelation: '', //lent to/deaccessioned to
    //         maxCrystalSize: '',
    //         notes: '',
    //         damages: '',
    //         selected: false, //@todo is this not better places in a view?
    //     };
    // },

    localAttributes: [
        'selected',
        'active',
        'modifiedTime',
        'descriptionHtml',
    ],

    isSelected: function() {
        return this.get('selected') === true;
    },

    getImageUrl: function(size, imageIndex) {
        size = size || '640x640';

        imageIndex = imageIndex || 0;

        var image = this.get('images')[imageIndex];
        if (image) {
            return image.baseUrl + size + '/' + image.fileName;
        }

        return null; //@todo return placeholder image
    },

    getImage: function(index) {
        index = index || 0;
        var image = this.get('images')[index];
        if (image) {
            return {
                baseUrl: image.baseUrl,
                fileName: image.fileName,
                aspectRatio: image.aspectRatio || 1.5,
            };
        }

        if (!this._placeHolderImage) {
            var variant = Math.floor((Math.random() * 4) + 1);
            var catalogType = this.collection.catalog.get('type');

            this._placeHolderImage = {
                baseUrl: '/images/placeholder/',
                fileName: 'item-placeholder-' + catalogType + '-' + variant + '.jpg',
                isPlaceholder: true,
                aspectRatio: 1.5,
            };
        }

        return this._placeHolderImage;
    },

    formatters: {
        group: function(field, value) {
            var text = [];
            _.each(field.sourceFields, function(sourceField){
                text.push(this.getTextValue(sourceField));
            }.bind(this))

            return _.compact(text).join(', ');
        },

        datetime: function(field, value) {
            return Html.formatDateTime(value);
        },

        string: function(field, value) {
            return _.escape(value);
        },

        text: function(field, value) {
            return _.escape(value);
        },

        bool: function(field, value) {
            return value ? 'true' : 'false'; //@todo translate/get labels from catalog
        },

        html: function(field, value) {
            return this.get(field.generatedFrom);
        },

        enum: function(field, value) {
            if (value === 'undefined') {
                return '';
            }

            var enumValue = _.findWhere(field.options.enumValues, {name: value});

            if (!enumValue) {
                console.warn('Object has enumValue (' + value + ') that is not available for field:', field.name);

                return '';
            }

            return enumValue.label || '';
        },

        colors: function(field, value) {
            var colors = _.map(this.get(field.name), function(color){
                return App.t('collection::object:details:color:' + color);
            });

            if (field.sourceFields && field.sourceFields.customColors) {
                colors.push(this.get(field.sourceFields.customColors));
            }

            return _.escape(colors.join(', '));
        },

        weight: function(field, value) {
            var unit = this.get(field.unitField);

            var weightValue = '';

            if (_.isNumber(value) && !_.isNaN(value)) {
                if (unit === 'lbs') {
                    weightValue = parseInt(value) + ' lbs';
                    if ((parseFloat(value) - Math.floor(parseFloat(value))) !== 0){
                        weightValue += ' ' + ((value - Math.floor(parseFloat(value))) * 16) + ' oz';
                    }
                } else {
                    weightValue = value + App.t('global::units-weights:' + unit + ':short');
                }
            }

            return weightValue;
        },

        length: function(field, value) {
            var unit = this.get(field.unitField);

            return value ? value + App.t('global::units-sizes:' + unit + ':short') : '';
        },

        monetaryValue: function(field, value) {
            var unit = this.get(field.unitField);

            return Html.formatCurrency(value, unit);
        },

        dimensions: function(field, value) {
            var fieldNames = field.sourceFields;

            var dimensionParts = _.compact([
                this.get(fieldNames.width),
                this.get(fieldNames.height),
                this.get(fieldNames.depth),
                this.get(fieldNames.unit),
            ]);

            return Html.formatDimensions.apply(Html, dimensionParts);
        },

        dimensionType: function(field, value) {
            return value ? App.t('collection::object:dimension-type:' + value) : '';
        },

        locality: function(field, value) {
            var locality = value;

            if (locality && locality.short) {
                locality = locality.short;
            }

            return locality ? _.escape(locality) : '';
        },

        // images: function(field, value) {
        // },

        // assets: function(field, value) {
        // },

        species: function(field, value) {
            var speciesNames = [];
            _.each(value, function(species){
                if (species.correlation) {
                    speciesNames.push(species.correlation);
                }

                speciesNames.push(species.name);
            })

            return _.escape(speciesNames.join(' '));
        },

        sets: function(field, value) {
            var setNames = _.map(this.getSets(), function(set){
                return set.get('name');
            });

            return _.escape(setNames.join(', '));
        },

        // storageId: function(field, value) {
        // },

        // status: function(field, value) {
        // },

        // history: function(field, value) {
        // },

        number: function(field, value) {
            var catalog = this.collection.catalog;
            var unitField = catalog.getFieldDefinition(field.unitField);

            var unitValue = this.get(field.unitField);

            var enumValue = _.findWhere(unitField.options.enumValues, {name: unitValue});

            if (!enumValue) {
                console.warn('Number unit has enumValue (' + unitValue + ') that is not available as a unit:', unitField.name);

                return '';
            }

            var unitLabel =  enumValue.label || _.escape(unitValue);

            return value ? _.escape(value) + ' ' + unitLabel : '';
        },
        //@addfield
    },

    getTextValue: function(fieldName) {
        var catalog = this.collection.catalog;

        var field = catalog.getFieldDefinition(fieldName);
        var value = this.getDeep(fieldName);

        if (!field) {
            console.warn('Missing fieldDefinition in Object::getTextValue() for field', fieldName);

            return value;
        }

        if (!this.formatters[field.displayType]) {
            console.warn('Missing Object::formatter for displayType', field.displayType, 'in field', fieldName);

            return value;
        }

        return this.formatters[field.displayType].bind(this)(field, value);
    },

    getMergedTextValues: function(fieldNames, separator) {
        separator = separator || ', ';

        var textValues = [];

        if (_.isString(fieldNames)) {
            fieldNames = [fieldNames];
        }

        if (_.isArray(fieldNames)) {
            _.each(fieldNames, function(fieldName){
                if (_.isString(fieldName)) {
                    textValues.push(this.getTextValue(fieldName));
                } else {
                    console.warn('Object::getMergedTextValues() needs to be called with string or array of strings');
                }
            }.bind(this))
        } else {
            console.warn('Object::getMergedTextValues() needs to be called with string or array of strings');
        }

        return _.compact(textValues).join(separator);
    },

    getHistoryValue: function(index, type) {
        var val = '';

        if (index < this.get('objectHistory').length) {
            val = this.get('objectHistory')[index][type] || '';
        }

        return val;
    },

    getDimensionType: function(width, height, unit) {
        var product = this.getDimensionsTypeAsNumber(width, height, unit);

        if (product == 0) {
            return '';
        }

        if (product < 1) {
            return 'micromount';
        } else if (product < 6.25) {
            return 'thumbnail';
        } else if (product < 25) {
            return 'miniature';
        } else if (product < 56.25) {
            return 'smallCabinet';
        } else if (product < 156.25) {
            return 'cabinetSpecimen';
        } else {
            return 'museumSpecimen';
        }
    },

    getDimensionsTypeAsNumber: function(width, height, unit){
        width = width ? width : this.get('sizeWidth');
        height = height ? height : this.get('sizeHeight');
        unit = unit ? unit : this.get('sizeUnit');

        var product = width * height;

        var sizeUnit = App.BaseModule.sizeUnitsCollection.get(unit);
        if (sizeUnit) {
            product = product * sizeUnit.get('factor') / 10;
        }

        return product;
    },

    /**
     * Returns an array of set models, which are assigned to the current object
     * @returns {Array} An array of the assigned sets or an empty array
     */
    getSets: function() {
        var sets = [];

        if (this.collection && this.collection.catalog.sets) {
            this.collection.catalog.sets.each(function (set) {
                for (var i = 0; i < set.get('items').length; i++) {
                    if (set.get('items')[i] === this.get('id')) {
                        sets.push(set);
                    }
                }
            }.bind(this));
        }

        return sets;
    },

    getRecentPurchasedHistoryEntry: function(){
        var purchasePrices = _.where(this.get('objectHistory'), {type: 'purchased'});

        return _.last(purchasePrices);
    },

    getRecentHistoryEntry: function(){
        var catalogFields = this.collection.catalog.get('fields');
        var historyField = _.findWhere(catalogFields, {displayType: 'history'});

        return historyField ? _.last(this.get(historyField.name)) : null;
    },

    getStoragePath: function() { //@todo return link list
        var storage = App.t('collection::object:details:storage:undefined');

        if (App.isShare()) {
            return storage;
        }

        var catalog = this.collection.catalog;
        var catalogFields = catalog.get('fields');
        var storageField = _.findWhere(catalogFields, {displayType: 'storageId'});

        var storageFieldName = storageField ? storageField.name : null;
        var storageModel = catalog.storages ? catalog.storages.get(this.get(storageFieldName)) : null;

        if (storageModel) {
            var path = storageModel.get('path').split('/'); //@todo names with / in it will be ripped apart
            var internalStorage = [];

            _.each(path, function(segment) {
                internalStorage.push(segment.trim());
            });

            storage = Html.list(internalStorage.join('\n'), 'ol');
        }

        return storage;
    },

    getConvertedMonetaryValue: function(fieldName, requestedCurrency) {
        var catalog = this.collection.catalog;

        var fieldDefinition = _.findWhere(catalog.get('fields'), {name: fieldName});
        var originalCurrency = this.get(fieldDefinition.unitField);
        var originalValue = this.get(fieldName);

        var originalCurrencyModel = App.radio().request('currencies:collection').findWhere({key: originalCurrency});
        var requestedCurrencyModel = App.radio().request('currencies:collection').findWhere({key: requestedCurrency});

        if (originalCurrencyModel && requestedCurrencyModel) {
            var originalPriceInEUR = originalValue / originalCurrencyModel.get('exchangeRate');

            return originalPriceInEUR * requestedCurrencyModel.get('exchangeRate');
        }

        return null;
    },

    getNormalizedValue: function(fieldName) {
        var value = this.get(fieldName);

        var catalog = this.collection.catalog;
        var fieldDefinition = _.findWhere(catalog.get('fields'), {name: fieldName});

        if (fieldDefinition && fieldDefinition.unitField) {
            var unitField = _.findWhere(catalog.get('fields'), {name: fieldDefinition.unitField});

            if (unitField.options && unitField.options.enumValues) {
                var unitValue = this.get(fieldDefinition.unitField);
                var enumValue = _.findWhere(unitField.options.enumValues, {name: unitValue});

                if (enumValue && enumValue.normalizationFactor) {
                    value = this.get(fieldName) * enumValue.normalizationFactor;
                }
            }
        }

        return value;
    },

    customAttributes: {
        properName: function() {
            var properName = _.compact([this.get('properName', true)]);

            var catalog = this.collection.catalog;
            var properNameFields = catalog.get('fieldsProperName');

            if (properName.length === 0 && properNameFields && _.isArray(properNameFields)) {
                _.each(properNameFields, function(properNameField) {
                    properName.push(this.getTextValue(properNameField));
                }.bind(this));

                properName = _.compact(properName);
            }

            return _.compact(properName).join(', ');
        },

        dimensionsTypeAsNumber: function(width, height, unit){ //@todo remove if obsolete
            return this.getDimensionsTypeAsNumber(width, height, unit);
        },

        obtained: function(){
            if (this.getRecentHistoryEntry()) {
                return App.t('collection::object:details:history:type:' + this.getRecentHistoryEntry().type + ':label');
            }

            return '';
        },

        obtainedDate: function(){
            if (this.getRecentHistoryEntry()) {
                return this.getRecentHistoryEntry().date;
            }

            return '';
        },

        obtainedFrom: function(){
            if (this.getRecentHistoryEntry()) {
                return this.getRecentHistoryEntry().from;
            }

            return '';
        },

        // purchasingPrice: function() {
        //     var mostRecentPurchasePrice = this.getRecentPurchasedHistoryEntry();

        //     if (mostRecentPurchasePrice && mostRecentPurchasePrice.price && mostRecentPurchasePrice.currency) {
        //         var mostRecentPurchaseCurrencyModel = App.radio().request('currencies:collection').findWhere({key: mostRecentPurchasePrice.currency});
        //         var userDefaultCurrencyModel = App.radio().request('currencies:collection').findWhere({key: App.user.getCurrency()});

        //         if (mostRecentPurchaseCurrencyModel) {
        //             var mostRecentPurchasePriceInEUR = mostRecentPurchasePrice.price / mostRecentPurchaseCurrencyModel.get('exchangeRate');

        //             return mostRecentPurchasePriceInEUR * userDefaultCurrencyModel.get('exchangeRate');
        //         }
        //     }

        //     return null;
        // },

        volume: function(){
            var depth  = this.get('sizeDepth')  || 1;
            var height = this.get('sizeHeight') || 1;
            var width  = this.get('sizeWidth')  || 1;

            return depth * height * width;
        },

        /**
         * Return the weight in g
         */
        normalizedWeight: function(){
            var weight = this.get('weight');
            var weightUnit = this.get('weightUnit');

            var weightUnitModel = App.BaseModule.weightUnitsCollection.findWhere({key: weightUnit});

            if (!_.isUndefined(weightUnitModel)) {
                return weight * weightUnitModel.get('factor');
            }

            return weight; // if there is no unit suppose that this is in g
        },

        weightConverted: function() {
            var weight = this.get('normalizedWeight');
            var userDefaultWeightUnitModel = App.BaseModule.weightUnitsCollection.findWhere({key: App.user.get('defaultWeightUnit')});

            return weight / userDefaultWeightUnitModel.get('factor');
        },

        findingDate: function() {
            var foundDates = _.pluck(_.where(this.get('objectHistory'), {type: 'found'}), 'date');
            var firstFoundDate = _.first(foundDates);

            if (firstFoundDate) {
                return App.util.getYear(firstFoundDate);
            }

            return null;
        },
    },
});

module.exports = ObjectModel;